import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import useBluetooth from '../../../shared/bluetooth-module/useBluetooth';
import useClientService from '../../../shared/services/useClientService';
import useWatchService from '../../../shared/services/useWatchService';
import { showToast } from '../../../shared/store/alerts';
import { useAppDispatch, useAppSelector } from '../../../shared/store/store';
import { CallState, LoadingState, SettingsData, Time } from '../../../shared/types';
import NewClientFormHeader from '../NewClientFormHeader';
import NewClientStep1 from '../NewClientStep1';
import NewClientStep2 from '../NewClientStep2';
import NewClientStep3 from '../NewClientStep3';
import { CancellationToken } from '../../../shared/helpers/CancellationToken';

const NewClientForm: React.FC<{
  handleCloseModal: () => void;
}> = ({ handleCloseModal }) => {
  const steps = [
    { label: ' User info', header: 'Add a new user' },
    { label: 'Connect watch', header: "Pair new user's watch" },
    { label: 'Sync', header: 'Synchronize data' },
  ];
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);

  // form for first step
  const {
    control,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      externalClientId: '',
      bpmForAge: 100, // select one as default; kind of a hack because placeholder doesn't populate on load
    },
  });

  // bluetooth for second step
  const [watchLoading, setWatchLoading] = useState(false);
  const bluetooth = useBluetooth();
  const { addEventLogData } = useWatchService();
  const [triggerHR, setTriggerHR] = useState(0);
  const [theClient, setTheClient] = useState<any>(null);

  // Step 2: connect to the watch to initiate pairing; leave connected
  const handleWatchPairing = async () => {
    try {
      if (!userProfile) return;
      setWatchLoading(true);
      setStoreConfigState(LoadingState.LOADING);

      // useBluetooth handles toasting all errors
      let {ready, device} = await bluetooth.connectNewBluetooth();
      if (!ready) {
        throw Error("Not ready for some reason"); // ready should never be false really
      }

      // Save BPM trigger based on page in local state
      const bpmForAge = getValues().bpmForAge ?? 75;
      const trig = Math.max(bpmForAge * 2, 100);
      setTriggerHR(trig)

      // Successful - now persist client to DB
      // This will toast & throw on error as well.
      await persistClient(device, trig);
    } catch(err) {
      console.log(err);
      bluetooth.disconnect();
    } finally {
      setWatchLoading(false);
    }
  };

  // Third step: sync
  const [storeConfigState, setStoreConfigState] = useState<CallState>(LoadingState.INIT);
  const [eventSyncState, setEventSyncState] = useState<CallState>(LoadingState.INIT);
  const [downloadPct, setDownloadPct] = useState(0);

  const handleStartSync = async () => {
    try {
      if (!userProfile) return;
      // TODO need watch
      setWatchLoading(true);
      setStoreConfigState(LoadingState.LOADING);

      console.log("Setting trigger HR to %d", triggerHR);
      // TODO: remove avg HR
      await bluetooth.setHeartRates(0, triggerHR);

      // FUTURE: other config to sync?
      setStoreConfigState(LoadingState.LOADED);
    } catch(err) {
      console.log(err);
      setStoreConfigState({error: "Error syncing config: " + err});
      bluetooth.disconnect();
      setWatchLoading(false);
      return;
    }

    try {
      const token = new CancellationToken();
      setOpCancel(token);
      setEventSyncState(LoadingState.LOADING);

      // Direct callbacks for download progress and save to DB
      // TODO: allow cancel
      await bluetooth.downloadCalooshaData(setDownloadPct, addEventLogData, token);
      setDownloadPct(1); //done

      setEventSyncState(LoadingState.LOADED);
    } catch(err) {
      console.log(err);
      setEventSyncState({error: "Failed to sync eventlog: " + err});
      bluetooth.disconnect();
    } finally {
      setWatchLoading(false);
      setOpCancel(null);
      // leave bluetooth on for follow-on screen
    }
  };


  // create client before last step
  const { userProfile } = useAppSelector((state) => state.profile);
  const { createClient } = useClientService();
  const [btLoading, setBtLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [opCancel, setOpCancel] = useState<CancellationToken|null>(null);

  const handleFinish = async () => {
    opCancel?.cancel();
    handleCloseModal();
    if (theClient != null) {
      navigate(`/dashboard/client/${theClient.id}/watch-settings`);
    }
  }

  const persistClient = async (dev:any, trigHR:number) => {
    // Careful can't use BT or HR state values yet
    try {
      if (!userProfile) return;
      setBtLoading(true);
      const settings: SettingsData = {
        restingHr: getValues().bpmForAge ?? 0, //XXX not used
        triggerHr: trigHR,
        // the following values may need to be defined by the client
        timeoutNavigation: 10,
        timeoutUserPrompt: 30,
        timeoutBluetooth: 60,
        numberOfCycles: 4,
        emojiPrompt1: new Time(9,0),
        emojiPrompt2: new Time(16,0),
        emojiPrompt3: null,
      };
      const { bpmForAge, ...valuesWithoutBPM } = getValues();
      const adaptedValues = {
        therapistId: userProfile.id,
        ...valuesWithoutBPM,
        serviceTypeId: 1, // not used, legal value
        dateOfBirth: new Date(2000,0,1), // not used
      };
      const client = await createClient(
        adaptedValues,
        dev,
        settings,
      );
      setTheClient(client);
      setBtLoading(false);
    } catch (error: any) {
      dispatch(
        showToast({
          summary: 'Error creating user',
          severity: 'error',
          detail: error.message || 'Unable to save user information to the database, please try again',
        }),
      );
      throw error;
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col">
      <NewClientFormHeader
        handleCloseModal={handleCloseModal}
        steps={steps}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
      />
      {activeIndex === 0 && (
        <NewClientStep1
          handleCloseModal={handleCloseModal}
          setActiveIndex={setActiveIndex}
          control={control}
          reset={reset}
          errors={errors}
          isValid={isValid}
        />
      )}
      {activeIndex === 1 && (
        <NewClientStep2
          setActiveIndex={setActiveIndex}
          handleConnectWatch={handleWatchPairing}
          handleStartSync={handleStartSync}
          watchLoading={watchLoading}
          btDevice={bluetooth.device}
        />
      )}
      {activeIndex === 2 && (
        <NewClientStep3
          setActiveIndex={setActiveIndex}
          handleSubmit={() => handleFinish()}
          triggerHR={triggerHR}
          storeConfigState={storeConfigState}
          eventSyncState={eventSyncState}
          downloadProgress={downloadPct}
        />
      )}
    </div>
  );
};

export default NewClientForm;
